import React, { useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { differenceInDays, differenceInMinutes, startOfDay } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import { toast } from "react-toastify";
import { useAuthContext } from "../../../lib/context/Auth/AuthContext";
import { useBookingsContext } from "../Context/BookingsContext";
import { useRouter } from "../../../lib/hooks/useRouter";
import { useReserveHelpers } from "../lib/reserveHelpers";
import { useOutsideClickDetection } from "../../../lib/hooks/useOutsideClickDetection";
import { useTimeZoneContext } from "../../../lib/context/TimeZone/TimeZoneContext";
import { useCalendarContext } from "../../../lib/context/Calendar/CalendarContext";
import { useBookingsMapContext } from "../BookingsMap/Context/BookingsMapContext";
import { useCalendarRequests } from "../../../api/grpc/calendarprovider/useCalendarRequests";
import {
  useFormatReservationDate as formatReservationDate,
  forReservationDate,
} from "../lib/datePickerHelper";

import { capitalizeFirstLetter } from "../../../lib/helpers/capitalizeFirstLetter";
import {
  getWorkplaceFragment,
  getWorkplaceFragmentName,
} from "../../Workplaces/helpers/getWorkplaceFragment";
import {
  attachHoursToDate,
  dateToHoursAndMinutes,
} from "../lib/dateInputConvert";
import { defaultAttendee } from "../../../lib/helpers/defaultRoomEvent";
import { defineLocationPath } from "../helpers/defineLocationPath";

import { BookingsModal } from "./BookingsModal/BookingsModal";
import { DropdownList } from "../../shared/DropdownList/DropdownList";
import { MatchedWorkplaceActionsType } from "../../../views/Bookings/types";
import { Icon, IconDefinition } from "../../../components/shared/Icon/Icon";
import { Attendee } from "../../../api/grpc/booking/ggevent/ggevent";
import { Button } from "../../../components/shared/Button/Button";
import { DeskLocationItem } from "../../../api/grpc/desk/desk";
import { CalendarCredentialsStatus } from "../../../api/grpc/calendarprovider/calendarprovider";
import { MainResources } from "../../../lib/types/main.types";
import { SearchDesk } from "../../../api/grpc/workplaces/workplaces";
import { RESERVATIONS_MAP_ROOT_PATH } from "../../../lib/routes/routes";
import { INFO_STRINGS } from "../../../lib/utils/constants";

interface Props {
  workspaceId: string;
  type: MainResources;
  icon?: IconDefinition;
  locationPath: DeskLocationItem[];
  assignedDesk?: SearchDesk;
  bookingWindow: number;
}

export const MatchedWorkplaceActions = ({
  workspaceId,
  type,
  icon,
  locationPath,
  assignedDesk,
  bookingWindow,
}: Props) => {
  const ref = useRef(null);
  const { history } = useRouter();
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAssignModal, setShownAssignModal] = useState(false);
  const client = useApolloClient();
  const { user } = useAuthContext();
  const { timeZone } = useTimeZoneContext();
  const { personalCalendar, refetchPersonalCalendar } = useCalendarContext();
  const { validateCalendarIntegrationCredentials } = useCalendarRequests();

  const { allDay, bookOnBehalfUser } = useBookingsMapContext();
  const {
    day,
    dates,
    reservDeskVariables: { startTime, endTime },
    attendees,
    subject,
    handleSubject,
    handleModalClose,
    isOnlineMeeting,
    makePrivate,
    matchedRooms,
  } = useBookingsContext();

  const exceedsBookingWindow = (dates.length ? dates : [day]).some(
    (selectedDate) =>
      differenceInDays(
        startOfDay(selectedDate),
        startOfDay(toZonedTime(new Date(), timeZone))
      ) > bookingWindow
  );

  const options = useMemo(
    () => [
      {
        icon: "reserve-icon",
        label: "Reserve",
        type: "reserve-workplace",
        disabled: exceedsBookingWindow,
      },
      {
        icon: "open-map-icon",
        label: "Open on map",
        type: "open-on-map",
      },
    ],
    [exceedsBookingWindow]
  );

  const {
    isOpen,
    toggleModal,
    handleReserveDesk,
    handleReserveMultiDayDesk,
    handleReserveRoom,
    locationPath: locationOfWorkplace,
    organizerName,
  } = useReserveHelpers();

  const workplaceData = client.readFragment({
    id: `${capitalizeFirstLetter(type)}:${workspaceId}`,
    fragmentName: getWorkplaceFragmentName(capitalizeFirstLetter(type), true),
    fragment: getWorkplaceFragment(capitalizeFirstLetter(type), true),
  });

  const timeDifference = differenceInMinutes(
    toZonedTime(new Date(), timeZone),
    startTime
  );

  const validTimePassed = timeDifference <= 5 && timeDifference > 0;
  let zonedDay = fromZonedTime(day, timeZone);

  let start = attachHoursToDate(
    toZonedTime(zonedDay, timeZone).toISOString(),
    validTimePassed ? toZonedTime(new Date(), timeZone) : startTime,
    timeZone
  );

  let end = attachHoursToDate(
    toZonedTime(zonedDay, timeZone).toISOString(),
    endTime,
    timeZone,
    allDay
  );

  const isMultiDayDeskBooking = type === "desk" && dates.length > 1;

  const multiDayTimeSlots = dates.map((selectedDate) => {
    const zonedSelectedDay = fromZonedTime(selectedDate, timeZone);

    return {
      startTime: attachHoursToDate(
        toZonedTime(zonedSelectedDay, timeZone).toISOString(),
        validTimePassed ? toZonedTime(new Date(), timeZone) : startTime,
        timeZone
      ),
      endTime: attachHoursToDate(
        toZonedTime(zonedSelectedDay, timeZone).toISOString(),
        endTime,
        timeZone,
        allDay
      ),
    };
  });

  // The main `day` prop on the confirmation modal already shows the earliest
  // date, so these are the rest of the selected dates for a multi-day desk
  // booking.
  const additionalReservationDays = isMultiDayDeskBooking
    ? [...dates]
        .sort((a, b) => a.getTime() - b.getTime())
        .slice(1)
        .map((selectedDate) => forReservationDate(selectedDate, timeZone))
    : undefined;

  const handleReserveAction = async ({
    type,
    typeOfWorkplace,
    showWarning,
  }: {
    type: MatchedWorkplaceActionsType;
    typeOfWorkplace: MainResources;
    showWarning?: boolean;
  }) => {
    setShowDropdown(false);

    setLoading(true);

    const eventIsForOtherUser = !!bookOnBehalfUser;

    if (type === "reserve-workplace") {
      if (workplaceData.isBlocked) {
        setLoading(false);
        return toast.error(
          `Reservations blocked for ${typeOfWorkplace} due to admin policy`
        );
      }

      if (showWarning) {
        setShownAssignModal(true);
        setLoading(false);

        return;
      }

      if (typeOfWorkplace === "desk") {
        const deskReserveArgs = {
          accountId: eventIsForOtherUser
            ? bookOnBehalfUser.accountID
            : user?.claims.user_id || "",
          customerID: user?.customerid || "",
          deskId: workspaceId,
          checkInReminderSend: workplaceData?.showCheckInTime,
          organizer: eventIsForOtherUser
            ? {
                ...defaultAttendee,
                email: bookOnBehalfUser.email,
              }
            : undefined,
        };

        if (isMultiDayDeskBooking) {
          await handleReserveMultiDayDesk({
            ...deskReserveArgs,
            dates: multiDayTimeSlots,
          });
        } else {
          await handleReserveDesk({
            ...deskReserveArgs,
            startTime: start,
            endTime: end,
          });
        }
        setLoading(false);

        return;
      }

      let roomToReserve = matchedRooms?.find((room) => room.id === workspaceId);

      if (!roomToReserve) {
        return toast.error("Room couldn't be found!");
      }

      let hasPersonalCalendar = personalCalendar.length > 0;
      let statusOfPersonalIntegrationToken;

      if (hasPersonalCalendar) {
        try {
          await refetchPersonalCalendar();

          const {
            response: { status },
          } = await validateCalendarIntegrationCredentials(
            personalCalendar[0].iD
          );

          statusOfPersonalIntegrationToken = status;
        } catch (error: any) {
          toast.error(error?.message);
          return;
        }
      }

      if (
        hasPersonalCalendar &&
        statusOfPersonalIntegrationToken ===
          CalendarCredentialsStatus.CalendarCredentialsStatusUnknown
      ) {
        return toast.info(
          "Couldn't define the status of the personal integration, please contact your administrator."
        );
      }

      const hasValidPersonalToken =
        statusOfPersonalIntegrationToken ===
        CalendarCredentialsStatus.CalendarCredentialsStatusValid;

      const isMode2Reservation = hasPersonalCalendar && hasValidPersonalToken;

      const organizerEmail = (): string => {
        if (eventIsForOtherUser) {
          return bookOnBehalfUser.email;
        }

        if (isMode2Reservation) {
          return user?.email || "";
        }

        return workplaceData?.resourceEmail;
      };

      const ateendeeEmail = (): string => {
        if (eventIsForOtherUser) {
          return bookOnBehalfUser.email;
        }

        if (isMode2Reservation) {
          return workplaceData?.resourceEmail;
        }

        return user?.email || "";
      };

      let attendee: Attendee[] = [
        {
          ...defaultAttendee,
          email: ateendeeEmail(),
        },
      ];

      attendees.map((item) => {
        return attendee.push({
          ...defaultAttendee,
          email: item,
        });
      });

      await handleReserveRoom({
        roomId: workspaceId,
        title: subject,
        attendees: attendee,
        onBehalfUserId: eventIsForOtherUser
          ? bookOnBehalfUser.accountID
          : undefined,
        calendarId:
          isMode2Reservation && !eventIsForOtherUser
            ? ""
            : roomToReserve.calendarID,
        calendarproviderId:
          isMode2Reservation && !eventIsForOtherUser
            ? personalCalendar[0].iD
            : roomToReserve.calendarProviderID,
        startTime: start,
        endTime: end,
        organizerEmail: organizerEmail(),
        checkInReminderSend: !!workplaceData?.displaySettings?.checkinReminder,
        isOnlineMeeting: isOnlineMeeting,
        isPrivate: makePrivate,
      });
      setLoading(false);

      return;
    }

    let workplaceParent = locationPath.filter(
      (workplace) => workplace.type === "Floor"
    );

    if (workplaceParent.length <= 0) {
      return toast.error("This workspace is not part of a floor!");
    }

    if (
      workplaceData.marker &&
      workplaceData.marker.latitude === 0 &&
      workplaceData.marker.latitude === 0
    ) {
      return toast.error(
        "Workspace not added to the floor plan map. Contact admin."
      );
    }

    history.push(
      `${RESERVATIONS_MAP_ROOT_PATH}/${workplaceParent[0].id}/markerOpen=${workspaceId}`
    );
  };

  const handleModalCloseHelper = () => {
    handleSubject("");
    toggleModal();
    handleModalClose();
  };

  useOutsideClickDetection(ref, () => {
    setShowDropdown(false);
  });

  const definedUsername = useMemo(() => {
    if (bookOnBehalfUser) {
      return bookOnBehalfUser.displayName || bookOnBehalfUser.email;
    }

    if (type === "desk" || !!!personalCalendar.length) {
      return user?.firstName + " " + user?.lastName;
    }

    return organizerName || "";
  }, [personalCalendar, organizerName]);

  return (
    <div ref={ref}>
      <div className="flex-a-center">
        <Button
          title={loading ? "Loading..." : "Reserve"}
          size="small"
          className="Reserve__button"
          disabled={exceedsBookingWindow}
          tooltip={
            exceedsBookingWindow ? INFO_STRINGS.outsideBookingWindow : undefined
          }
          onClick={() =>
            handleReserveAction({
              type: "reserve-workplace",
              typeOfWorkplace: type,
              showWarning: type === "desk" && !!assignedDesk,
            })
          }
        />
        <Icon
          icon="actions-icon"
          onClick={() => setShowDropdown((prev) => !prev)}
        />
      </div>

      {showDropdown && (
        <DropdownList
          extended
          color="gray"
          options={options}
          onClick={(value) =>
            handleReserveAction({
              type: value as MatchedWorkplaceActionsType,
              typeOfWorkplace: type,
              showWarning: type === "desk" && !!assignedDesk,
            })
          }
        />
      )}

      {showAssignModal && (
        <BookingsModal
          isOpen={showAssignModal}
          toggle={() => setShownAssignModal(false)}
          title="Heads up!"
          type="warning"
          workspaceType="desk"
          day=""
          endTime=""
          startTime=""
          classNames="modal-footer-row modal-footer-row-reverse"
          locationPath={
            assignedDesk?.locationPath.length
              ? assignedDesk.locationPath
              : [{ id: "1", index: 0, name: "All locations", type: "Root" }]
          }
          userName={definedUsername + `${bookOnBehalfUser ? "" : " (you)"}`}
          workspaceName={assignedDesk?.name || ""}
          everyDay={true}
          buttonTitle="Yes"
          buttonCancelTitle="No"
          handleModalClose={() => setShownAssignModal(false)}
          buttonClick={() => {
            setShownAssignModal(false);

            handleReserveAction({
              type: "reserve-workplace",
              typeOfWorkplace: type,
              showWarning: false,
            });
          }}
        />
      )}

      {isOpen && (
        <BookingsModal
          isOpen={isOpen}
          toggle={toggleModal}
          workspaceType={type}
          title={
            isMultiDayDeskBooking
              ? `${dates.length} reservations confirmed`
              : "Reservation confirmed"
          }
          icon="reservation-confimed"
          day={formatReservationDate(toZonedTime(new Date(start), timeZone))}
          additionalDays={additionalReservationDays}
          workspaceName={workplaceData.name}
          startTime={dateToHoursAndMinutes(new Date(start), timeZone)}
          endTime={dateToHoursAndMinutes(new Date(end), timeZone)}
          userName={definedUsername}
          subject={subject}
          locationPath={defineLocationPath(locationOfWorkplace)}
          handleModalClose={handleModalCloseHelper}
          type="confirm"
          allDay={allDay}
        />
      )}
    </div>
  );
};
